import { createFileRoute, useNavigate, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'motion/react';
import { useAuth } from '@/contexts/AuthContext';
import { CircularLoader } from '@/components/CircularLoader';
import styles from './auth.module.scss';

export const Route = createFileRoute('/login')({
  beforeLoad: ({ context }: { context: any }) => {
    // If user is already logged in, redirect to home
    if (context.authLoading) return;
    if (context.user) {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);

    const { error: signInError } = await signIn(email, password);

    setLoading(false);

    if (signInError) {
      // Handle Firebase-specific errors
      const firebaseError = signInError as any;
      if (firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/wrong-password') {
        setError('Invalid email or password');
      } else if (firebaseError.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (firebaseError.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later');
      } else {
        setError(signInError.message);
      }
    } else {
      // Navigate to home on successful login
      navigate({ to: '/' });
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);

    const { error: googleError } = await signInWithGoogle();

    setGoogleLoading(false);

    if (googleError) {
      // Handle Google Sign-In errors
      const firebaseError = googleError as any;
      if (firebaseError.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else if (firebaseError.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups for this site');
      } else {
        setError(googleError.message);
      }
    } else {
      // Navigate to home on successful login
      navigate({ to: '/' });
    }
  };

  return (
    <div className={styles.authContainer}>
      <motion.div
        className={styles.authCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className={styles.authCard__header}>
          <h1 className={styles.authCard__title}>Welcome Back</h1>
          <p className={styles.authCard__subtitle}>
            Log in to continue your daily review
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.authCard__form}>
          <div className={styles.authCard__field}>
            <label htmlFor="email" className={styles.authCard__label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.authCard__input}
              placeholder="you@example.com"
              disabled={loading || googleLoading}
              autoComplete="email"
            />
          </div>

          <div className={styles.authCard__field}>
            <label htmlFor="password" className={styles.authCard__label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.authCard__input}
              placeholder="Enter your password"
              disabled={loading || googleLoading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <motion.div
              className={styles.authCard__error}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              {error}
            </motion.div>
          )}

          <div className={styles.authCard__buttonGroup}>
            <motion.button
              type="submit"
              className={styles.authCard__button}
              disabled={loading || googleLoading}
              whileHover={{ scale: (loading || googleLoading) ? 1 : 1.02 }}
              whileTap={{ scale: (loading || googleLoading) ? 1 : 0.98 }}
            >
              {loading ? (
                <span className={styles.authCard__buttonContent}>
                  <CircularLoader className={styles.authCard__loader} />
                  Logging in...
                </span>
              ) : (
                'Log In'
              )}
            </motion.button>

            <motion.button
              type="button"
              onClick={handleGoogleSignIn}
              className={`${styles.authCard__button} ${styles.authCard__button__google}`}
              disabled={loading || googleLoading}
              whileHover={{ scale: (loading || googleLoading) ? 1 : 1.02 }}
              whileTap={{ scale: (loading || googleLoading) ? 1 : 0.98 }}
            >
              {googleLoading ? (
                <span className={styles.authCard__buttonContent}>
                  <CircularLoader className={styles.authCard__loader} />
                  Signing in with Google...
                </span>
              ) : (
                <span className={styles.authCard__buttonContent}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.837.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z" fill="#34A853"/>
                    <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.426 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </span>
              )}
            </motion.button>
          </div>

          <div className={styles.authCard__footer}>
            Don't have an account?{' '}
            <Link to="/signup" className={styles.authCard__link}>
              Sign up
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
